/**
 * Prevents mixing pre-order products (SKU starting with "PRE-") with regular
 * products in the same cart. Two layers, both front-end (works on any plan):
 *
 *  1. Proactive product-page block — when the cart already holds the other
 *     category, the Add to cart button is disabled, the dynamic checkout
 *     (Shop Pay / Google Pay / Apple Pay) buttons are hidden, and a message is
 *     shown above the buy button explaining why.
 *
 *  2. Reactive add-to-cart guard — a safety net for quick-add / drawer / any
 *     other add path: it intercepts /cart/add, and if the add would create a
 *     mix it removes the just-added item and shows a warning toast.
 */
(function () {
  'use strict';

  const PREORDER_SKU_PREFIX = 'PRE';

  const COPY = {
    addingPreorder: {
      title: 'Pre-order items ship separately',
      body: 'Your cart has in-stock items, so this pre-order can’t be added. Check out your current items first, then place the pre-order on its own.',
    },
    addingRegular: {
      title: 'Finish your pre-order separately',
      body: 'Your cart has a pre-order, which ships on its own. Place that order first, then come back for in-stock items.',
    },
  };
  const VIEW_CART_LABEL = 'View cart';

  const PRODUCT_BLOCK_COPY = {
    cartHasPreorder:
      'You already have a pre-order product in your cart. This product can’t be purchased together with a pre-order — please order them separately.',
    cartHasRegular:
      'You can’t purchase a pre-order product together with the in-stock item already in your cart — please order them separately.',
  };

  const CART_COPY = {
    notice:
      'Your cart contains both a pre-order and an in-stock product. They must be purchased separately — please remove one to continue to checkout.',
    toast: {
      title: 'Can’t check out a mixed cart',
      body: 'Your cart has both a pre-order and in-stock items. Please order them separately before checking out.',
    },
  };
  // -------------------------------------------------------------------------

  const routes = (window.Theme && window.Theme.routes) || {};
  const CART_URL = routes.cart_url || '/cart';
  const CHANGE_URL = (routes.cart_change_url || '/cart/change') + '.js';
  const CART_JS_URL = CART_URL.replace(/\/$/, '') + '.js';
  const ADD_URL = routes.cart_add_url || '/cart/add';

  const nativeFetch = window.fetch.bind(window);

  const isPreorderSku = (sku) =>
    typeof sku === 'string' && sku.toUpperCase().startsWith(PREORDER_SKU_PREFIX);

  function classify(cart) {
    let hasPreorder = false;
    let hasRegular = false;
    for (const item of cart.items || []) {
      if (isPreorderSku(item.sku)) hasPreorder = true;
      else hasRegular = true;
    }
    return { hasPreorder, hasRegular };
  }

  const isMixed = (c) => c.hasPreorder && c.hasRegular;

  function quantitiesByKey(cart) {
    const map = {};
    for (const item of cart.items || []) map[item.key] = item.quantity;
    return map;
  }

  async function getCart() {
    const res = await nativeFetch(CART_JS_URL, {
      headers: { Accept: 'application/json' },
    });
    return res.json();
  }

  async function rollback(before, after) {
    const prev = quantitiesByKey(before);
    let addedSku = null;
    for (const item of after.items || []) {
      const priorQty = prev[item.key] || 0;
      if (item.quantity > priorQty) {
        if (!addedSku) addedSku = item.sku;
        await nativeFetch(CHANGE_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({ id: item.key, quantity: priorQty }),
        });
      }
    }
    return addedSku;
  }

  function isAddRequest(input, init) {
    const url = typeof input === 'string' ? input : input && input.url;
    if (!url) return false;
    const method = (init && init.method) || (input && input.method) || 'GET';
    return method.toUpperCase() === 'POST' && url.indexOf(ADD_URL) !== -1;
  }

  let stylesInjected = false;
  function injectStyles() {
    if (stylesInjected) return;
    stylesInjected = true;
    const css = `
      .preorder-guard-hidden{display:none !important;}
      add-to-cart-component button[name="add"].preorder-guard-disabled,
      [name="checkout"].preorder-guard-disabled,
      .cart__checkout-button.preorder-guard-disabled{
        opacity:.45;cursor:not-allowed;pointer-events:none;
      }
      #preorder-guard-notice,
      .preorder-guard-cart-notice{
        display:flex;gap:8px;align-items:flex-start;
        margin:0 0 12px;padding:10px 12px;
        background:rgba(194,65,12,.07);
        border:1px solid rgba(194,65,12,.28);
        border-radius:8px;color:#7c2d12;
        font-size:13px;line-height:1.45;
      }
      #preorder-guard-notice .pg-n-icon,
      .preorder-guard-cart-notice .pg-n-icon{
        flex:0 0 auto;width:18px;height:18px;margin-top:1px;color:#c2410c;
      }
      #preorder-guard-toast{
        position:fixed;z-index:2147483647;right:20px;bottom:20px;
        width:min(380px,calc(100vw - 32px));
        display:flex;gap:12px;align-items:flex-start;
        padding:16px 16px 16px 14px;
        background:#fff;color:#1a1a1a;
        border:1px solid rgba(0,0,0,.08);border-left:3px solid #c2410c;
        border-radius:12px;box-shadow:0 12px 32px rgba(0,0,0,.16);
        font-family:inherit;
        opacity:0;transform:translateY(12px);
        transition:opacity .24s ease,transform .24s ease;
      }
      #preorder-guard-toast.is-visible{opacity:1;transform:translateY(0);}
      #preorder-guard-toast .pg-icon{
        flex:0 0 auto;width:22px;height:22px;margin-top:1px;color:#c2410c;
      }
      #preorder-guard-toast .pg-body{flex:1 1 auto;min-width:0;}
      #preorder-guard-toast .pg-title{
        margin:0 0 3px;font-size:14px;font-weight:600;letter-spacing:-.01em;line-height:1.3;
      }
      #preorder-guard-toast .pg-text{
        margin:0;font-size:13px;line-height:1.45;color:#4b4b4b;
      }
      #preorder-guard-toast .pg-actions{margin-top:10px;}
      #preorder-guard-toast .pg-link{
        display:inline-block;font-size:13px;font-weight:600;color:#c2410c;
        text-decoration:none;border-bottom:1px solid currentColor;padding-bottom:1px;
      }
      #preorder-guard-toast .pg-close{
        flex:0 0 auto;appearance:none;border:0;background:transparent;cursor:pointer;
        padding:2px;margin:-4px -4px 0 0;color:#9a9a9a;line-height:0;border-radius:6px;
      }
      #preorder-guard-toast .pg-close:hover{color:#1a1a1a;background:rgba(0,0,0,.05);}
      @media (prefers-reduced-motion:reduce){
        #preorder-guard-toast{transition:opacity .24s ease;transform:none;}
        #preorder-guard-toast.is-visible{transform:none;}
      }
    `;
    const style = document.createElement('style');
    style.id = 'preorder-guard-styles';
    style.textContent = css;
    document.head.appendChild(style);
  }

  const WARN_ICON_TOAST =
    '<svg class="pg-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
    '<path d="M12 9v4m0 4h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" ' +
    'stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  const WARN_ICON_NOTICE =
    '<svg class="pg-n-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
    '<path d="M12 9v4m0 4h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" ' +
    'stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  const CLOSE_ICON =
    '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true">' +
    '<path d="M18 6 6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';

  let toastEl = null;
  let hideTimer = null;

  function hideToast() {
    if (toastEl) toastEl.classList.remove('is-visible');
  }

  function showToast(copy) {
    injectStyles();
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.id = 'preorder-guard-toast';
      toastEl.setAttribute('role', 'alert');
      toastEl.setAttribute('aria-live', 'assertive');
      document.body.appendChild(toastEl);
    }
    toastEl.innerHTML =
      WARN_ICON_TOAST +
      '<div class="pg-body">' +
      '<p class="pg-title"></p>' +
      '<p class="pg-text"></p>' +
      '<div class="pg-actions"><a class="pg-link" href="' +
      CART_URL +
      '">' +
      VIEW_CART_LABEL +
      '</a></div>' +
      '</div>' +
      '<button class="pg-close" type="button" aria-label="Dismiss">' +
      CLOSE_ICON +
      '</button>';

    toastEl.querySelector('.pg-title').textContent = copy.title;
    toastEl.querySelector('.pg-text').textContent = copy.body;
    toastEl.querySelector('.pg-close').addEventListener('click', hideToast);

    requestAnimationFrame(() => toastEl.classList.add('is-visible'));
    clearTimeout(hideTimer);
    hideTimer = setTimeout(hideToast, 8000);
  }

  function blockedResponse(copy) {
    const body = JSON.stringify({
      status: 422,
      message: copy.title,
      description: copy.body,
    });
    return new Response(body, {
      status: 422,
      headers: { 'Content-Type': 'application/json' },
    });
  }

  window.fetch = async function (input, init) {
    if (!isAddRequest(input, init)) {
      return nativeFetch(input, init);
    }

    let before;
    try {
      before = await getCart();
    } catch (e) {
      return nativeFetch(input, init); // fail open
    }

    const response = await nativeFetch(input, init);
    if (!response.ok) return response;

    let after;
    try {
      after = await getCart();
    } catch (e) {
      return response;
    }

    if (!isMixed(classify(after))) {
      evaluateProductPage();
      evaluateCart();
      return response; // no mix created
    }

    let addedSku = null;
    try {
      addedSku = await rollback(before, after);
    } catch (e) {
      /* best effort */
    }
    const copy = isPreorderSku(addedSku) ? COPY.addingPreorder : COPY.addingRegular;
    showToast(copy);
    evaluateProductPage();
    evaluateCart();
    return blockedResponse(copy);
  };

  let currentSku;

  function onProductPage() {
    return /\/products\//.test(location.pathname);
  }

  function mainAddToCart() {
    return (
      Array.from(document.querySelectorAll('add-to-cart-component')).find(
        (el) => !el.closest('.quick-add-modal, sticky-add-to-cart, dialog, cart-drawer')
      ) || null
    );
  }

  async function fetchSelectedSku() {
    const m = location.pathname.match(/\/products\/([^/?#]+)/);
    if (!m) return null;
    try {
      const res = await nativeFetch('/products/' + m[1] + '.js', {
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) return null;
      const product = await res.json();
      const variants = product.variants || [];
      const vid = new URLSearchParams(location.search).get('variant');
      let v = null;
      if (vid) v = variants.find((x) => String(x.id) === String(vid));
      if (!v) v = variants.find((x) => x.available) || variants[0];
      return v ? v.sku : null;
    } catch (e) {
      return null;
    }
  }

  function applyBlock(message) {
    injectStyles();

    document
      .querySelectorAll('add-to-cart-component button[name="add"]')
      .forEach((btn) => {
        if (!btn.disabled) {
          btn.dataset.preorderGuardDisabled = '1';
          btn.disabled = true;
        }
        btn.classList.add('preorder-guard-disabled');
      });

    document
      .querySelectorAll('.accelerated-checkout-block, .shopify-payment-button')
      .forEach((el) => el.classList.add('preorder-guard-hidden'));

    const anchor = mainAddToCart();
    let notice = document.getElementById('preorder-guard-notice');
    if (!notice) {
      notice = document.createElement('div');
      notice.id = 'preorder-guard-notice';
      notice.setAttribute('role', 'alert');
      notice.innerHTML = WARN_ICON_NOTICE + '<span class="pg-n-text"></span>';
      if (anchor && anchor.parentNode) {
        anchor.parentNode.insertBefore(notice, anchor);
      } else {
        return;
      }
    }
    notice.querySelector('.pg-n-text').textContent = message;
  }

  function clearBlock() {
    document
      .querySelectorAll('add-to-cart-component button[name="add"].preorder-guard-disabled')
      .forEach((btn) => {
        if (btn.dataset.preorderGuardDisabled === '1') {
          btn.disabled = false;
          delete btn.dataset.preorderGuardDisabled;
        }
        btn.classList.remove('preorder-guard-disabled');
      });

    document
      .querySelectorAll('.preorder-guard-hidden')
      .forEach((el) => el.classList.remove('preorder-guard-hidden'));

    const notice = document.getElementById('preorder-guard-notice');
    if (notice) notice.remove();
  }

  async function evaluateProductPage() {
    if (!onProductPage()) return;
    if (currentSku === undefined) currentSku = await fetchSelectedSku();
    if (!currentSku) {
      clearBlock();
      return;
    }
    let cart;
    try {
      cart = classify(await getCart());
    } catch (e) {
      return;
    }
    const productIsPreorder = isPreorderSku(currentSku);
    if (productIsPreorder && cart.hasRegular) {
      applyBlock(PRODUCT_BLOCK_COPY.cartHasRegular);
    } else if (!productIsPreorder && cart.hasPreorder) {
      applyBlock(PRODUCT_BLOCK_COPY.cartHasPreorder);
    } else {
      clearBlock();
    }
  }

  let cartMixed = false;

  const CHECKOUT_SELECTOR =
    '[name="checkout"], .cart__checkout-button, .additional-checkout-buttons, ' +
    '.shopify-payment-button, .accelerated-checkout-block';

  function applyCartBlock() {
    injectStyles();

    document
      .querySelectorAll('[name="checkout"], .cart__checkout-button')
      .forEach((btn) => {
        btn.classList.add('preorder-guard-disabled');
        if (btn.tagName === 'BUTTON' && !btn.disabled) {
          btn.dataset.preorderGuardCheckout = '1';
          btn.disabled = true;
        } else {
          btn.setAttribute('aria-disabled', 'true');
        }
      });

    document
      .querySelectorAll('.additional-checkout-buttons, .shopify-payment-button')
      .forEach((el) => el.classList.add('preorder-guard-hidden'));

    document.querySelectorAll('[name="checkout"]').forEach((btn) => {
      const prev = btn.previousElementSibling;
      if (prev && prev.classList && prev.classList.contains('preorder-guard-cart-notice')) {
        prev.querySelector('.pg-n-text').textContent = CART_COPY.notice;
        return;
      }
      const notice = document.createElement('div');
      notice.className = 'preorder-guard-cart-notice';
      notice.setAttribute('role', 'alert');
      notice.innerHTML = WARN_ICON_NOTICE + '<span class="pg-n-text"></span>';
      notice.querySelector('.pg-n-text').textContent = CART_COPY.notice;
      if (btn.parentNode) btn.parentNode.insertBefore(notice, btn);
    });
  }

  function clearCartBlock() {
    document
      .querySelectorAll('[name="checkout"].preorder-guard-disabled, .cart__checkout-button.preorder-guard-disabled')
      .forEach((btn) => {
        btn.classList.remove('preorder-guard-disabled');
        if (btn.dataset.preorderGuardCheckout === '1') {
          btn.disabled = false;
          delete btn.dataset.preorderGuardCheckout;
        }
        btn.removeAttribute('aria-disabled');
      });
    document.querySelectorAll('.preorder-guard-cart-notice').forEach((n) => n.remove());
  }

  async function evaluateCart() {
    let cart;
    try {
      cart = classify(await getCart());
    } catch (e) {
      return;
    }
    cartMixed = isMixed(cart);
    if (cartMixed) applyCartBlock();
    else clearCartBlock();
  }

  document.addEventListener(
    'click',
    (e) => {
      if (!cartMixed) return;
      const target = e.target.closest && e.target.closest(CHECKOUT_SELECTOR);
      if (target) {
        e.preventDefault();
        e.stopImmediatePropagation();
        showToast(CART_COPY.toast);
      }
    },
    true
  );

  document.addEventListener('variant:update', (e) => {
    const sku = e.detail && e.detail.resource && e.detail.resource.sku;
    if (sku !== undefined) currentSku = sku;
    evaluateProductPage();
  });
  document.addEventListener('variant:selected', (e) => {
    const sku = e.detail && e.detail.resource && e.detail.resource.sku;
    if (sku !== undefined) currentSku = sku;
    evaluateProductPage();
  });
  document.addEventListener('cart:update', () => {
    evaluateProductPage();
    evaluateCart();
  });
  window.addEventListener('popstate', () => {
    currentSku = undefined;
    evaluateProductPage();
    evaluateCart();
  });

  function init() {
    evaluateProductPage();
    evaluateCart();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
